import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';

import {
  CHAT_LIMITS,
  ChatChannel,
  type ChatHistoryPayload,
  type ChatMessage,
} from '@waldermarch/shared';

/**
 * Chat state.
 *
 * Everything arrives over the existing game socket, so this store holds no
 * fetching of its own: `useWebSocket` pushes into it and the dock reads from
 * it. Sending goes back out through the same socket, which is why `sendVia` is
 * injected rather than imported - importing the socket module here would make
 * a cycle, since the socket module already imports this store.
 */
export const useChatStore = defineStore('chat', () => {
  const world = shallowRef<ChatMessage[]>([]);
  const alliance = shallowRef<ChatMessage[]>([]);
  const allianceName = ref<string | null>(null);
  const active = ref<ChatChannel>(ChatChannel.WORLD);
  const open = ref(false);
  /** Unread since the dock was last open and showing that room. */
  const unread = ref<Record<ChatChannel, number>>({
    [ChatChannel.WORLD]: 0,
    [ChatChannel.ALLIANCE]: 0,
  });

  const hasAlliance = computed(() => allianceName.value !== null);
  const messages = computed(() =>
    active.value === ChatChannel.ALLIANCE ? alliance.value : world.value,
  );
  const totalUnread = computed(
    () => unread.value[ChatChannel.WORLD] + unread.value[ChatChannel.ALLIANCE],
  );

  let send: ((channel: ChatChannel, body: string) => void) | null = null;

  function sendVia(fn: (channel: ChatChannel, body: string) => void): void {
    send = fn;
  }

  function applyHistory(payload: ChatHistoryPayload): void {
    allianceName.value = payload.allianceName;
    for (const room of payload.channels) {
      if (room.channel === ChatChannel.ALLIANCE) alliance.value = room.messages;
      else world.value = room.messages;
    }
    // A reconnect replays history; nothing in it is new to the player.
    unread.value = { [ChatChannel.WORLD]: 0, [ChatChannel.ALLIANCE]: 0 };
    // Losing an alliance mid-session should not strand the dock on a room
    // that is no longer there.
    if (!payload.allianceName && active.value === ChatChannel.ALLIANCE) {
      active.value = ChatChannel.WORLD;
    }
  }

  function receive(message: ChatMessage): void {
    const target = message.channel === ChatChannel.ALLIANCE ? alliance : world;
    const next = [...target.value, message];
    if (next.length > CHAT_LIMITS.HISTORY) next.splice(0, next.length - CHAT_LIMITS.HISTORY);
    target.value = next;

    const watching = open.value && active.value === message.channel;
    if (!watching) unread.value = { ...unread.value, [message.channel]: unread.value[message.channel] + 1 };
  }

  function clearUnread(channel: ChatChannel): void {
    if (unread.value[channel] === 0) return;
    unread.value = { ...unread.value, [channel]: 0 };
  }

  function setOpen(next: boolean): void {
    open.value = next;
    if (next) clearUnread(active.value);
  }

  function setActive(channel: ChatChannel): void {
    active.value = channel;
    if (open.value) clearUnread(channel);
  }

  function post(body: string): void {
    const text = body.replace(/\s+/g, ' ').trim().slice(0, CHAT_LIMITS.BODY_MAX);
    if (!text || !send) return;
    send(active.value, text);
  }

  /** On sign-out the next player must not inherit the last one's rooms. */
  function reset(): void {
    world.value = [];
    alliance.value = [];
    allianceName.value = null;
    active.value = ChatChannel.WORLD;
    open.value = false;
    unread.value = { [ChatChannel.WORLD]: 0, [ChatChannel.ALLIANCE]: 0 };
  }

  return {
    world,
    alliance,
    allianceName,
    hasAlliance,
    active,
    open,
    unread,
    totalUnread,
    messages,
    sendVia,
    applyHistory,
    receive,
    clearUnread,
    setOpen,
    setActive,
    post,
    reset,
  };
});
